import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { useLang } from "../i18n/LanguageContext";
import { images } from "../content/siteConfig";

const LayerDiagram = () => {
  const { t } = useLang();
  const [spread, setSpread] = useState(false);
  const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
  const layerColors = ["rgba(25,199,193,0.85)", "rgba(25,199,193,0.45)", "rgba(9,43,53,0.9)"];

  return (
    <div
      className="relative select-none cursor-pointer py-10"
      onMouseEnter={canHover ? () => setSpread(true) : undefined}
      onMouseLeave={canHover ? () => setSpread(false) : undefined}
      onClick={canHover ? undefined : () => setSpread((s) => !s)}
      role="button"
      tabIndex={0}
      aria-pressed={spread}
      onKeyDown={(e) => e.key === "Enter" && setSpread((s) => !s)}
      aria-label={t.a11y.filmDiagram}
      data-testid="film-layer-diagram"
    >
      <div className="flex flex-col md:flex-row items-center justify-center md:gap-14 lg:gap-20">
        <div className="relative h-[300px] md:h-[340px] w-full max-w-[340px] md:max-w-[380px] shrink-0">
          {t.tech.structure.layers.map((layer, i) => (
            <div
              key={layer.title}
              className="absolute left-1/2 top-1/2 w-[240px] md:w-[300px] h-[70px] md:h-[80px] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{
                transform: `translate(-50%, -50%) translateY(${spread ? (i - 1) * 105 : (i - 1) * 14}px) rotateX(55deg) rotateZ(-32deg)`,
                background: layerColors[i],
                boxShadow: "0 18px 40px rgba(6,26,38,0.25)",
                backdropFilter: "blur(2px)",
              }}
            />
          ))}
        </div>
        <div className="hidden md:flex flex-col justify-center w-[300px] lg:w-[340px] shrink-0" data-testid="film-layer-annotations">
          {t.tech.structure.layers.map((layer, i) => (
            <div key={layer.title} className="flex items-center h-[105px]">
              <div>
                <div className="flex items-center gap-3">
                  <span className="w-3.5 h-3.5 shrink-0" style={{ background: layerColors[i] }} aria-hidden="true" />
                  <h4 className="text-[17px] font-semibold tracking-normal text-[#061A26]">{layer.title}</h4>
                </div>
                <p className="mt-1.5 text-[15px] leading-[1.5] text-slate-500">{layer.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="md:hidden mt-8 space-y-5 max-w-md mx-auto">
        {t.tech.structure.layers.map((layer, i) => (
          <div key={layer.title} className="flex gap-4 items-start">
            <span className="w-3.5 h-3.5 mt-1 shrink-0" style={{ background: layerColors[i] }} aria-hidden="true" />
            <div>
              <p className="text-base font-semibold tracking-normal text-[#061A26]">{layer.title}</p>
              <p className="mt-1 text-[15px] leading-[1.5] text-slate-500">{layer.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-400 italic text-center">{t.tech.structure.note}</p>
    </div>
  );
};

export default function TechnologyPage() {
  const { t, path } = useLang();
  const blocks = [
    { key: "experience", data: t.tech.experience },
    { key: "portability", data: t.tech.portability },
    { key: "development", data: t.tech.development },
    { key: "commercial", data: t.tech.commercial },
  ];

  return (
    <>
      <SEO page="tech" />
      <PageHero eyebrow={t.tech.eyebrow} title={t.tech.title} sub={t.tech.sub} testId="tech-hero" />

      <section className="bg-[#F7F8F5] py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <div className="w-14 h-px bg-[#19C7C1] mb-8" />
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#061A26]">{t.tech.what.title}</h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">{t.tech.what.copy}</p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
            <img src={images.techLight} alt={t.a11y.techImageAlt} loading="lazy" className="w-full h-72 object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32 border-t border-black/5">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-[#061A26]">{t.tech.structure.title}</h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">{t.tech.structure.copy}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <LayerDiagram />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F7F8F5] py-24 md:py-32 border-t border-black/5">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/5 border border-black/5">
          {blocks.map((b, i) => (
            <Reveal key={b.key} delay={i * 0.08} y={18} className="bg-[#F7F8F5] p-10 md:p-12" data-testid={`tech-block-${b.key}`}>
              <span className="text-xs font-semibold tracking-[0.3em] text-[#19C7C1] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-4 text-lg md:text-xl font-semibold text-[#061A26]">{b.data.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.data.copy}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#092B35] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 film-lines opacity-40" aria-hidden="true" />
        <div className="relative max-w-[900px] mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-light tracking-tight text-white">{t.tech.regulatory.title}</h2>
            <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-2xl">{t.tech.regulatory.copy}</p>
            <Link to={path("/contact?interest=technical")} className="btn-primary mt-10 inline-flex" data-testid="tech-request-info-cta">
              {t.tech.cta}
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
